import { useMsal } from '@azure/msal-react';
import { useHeap } from '@configs/heap';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { useGetToken } from '@shared/hooks/use-get-token/useGetToken';
import * as React from 'react';

import type { ReservationsAlertsProps } from './ReservationsAlerts.types';
import type { ReservationsAlertsTilesProps } from './ReservationsAlertsTiles.types';

import { AlertTile } from '../alert-tile/AlertTile';
import {
  ALERT_LABEL_MAP,
  ALERT_VARIANT_MAP,
} from './ReservationsAlertsTiles.utils';

const ReservationsAlertsTiles = React.memo(
  ({
    onAlertSelect,
    onStateChange,
  }: Readonly<ReservationsAlertsTilesProps>): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const { selectedOrganization } = useOrganizations();
    const getToken = useGetToken();
    const heapIdentity = useHeap();

    const [RemoteComponent, setRemoteComponent] =
      React.useState<React.ComponentType<ReservationsAlertsProps> | null>(null);
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    const loadComponent = React.useCallback(async (): Promise<void> => {
      try {
        const { default: Component } =
          await import('reservations_mfe/ReservationsDashboardAlerts');
        setRemoteComponent(() => Component);
      } catch (error) {
        setLoadError(error as Error);
      }
    }, []);

    React.useEffect(() => {
      void loadComponent();
    }, [loadComponent]);

    React.useEffect(() => {
      if (loadError) {
        onStateChange({ alerts: [], isLoading: false, isError: true });
      }
    }, [loadError, onStateChange]);

    if (
      loadError ||
      !RemoteComponent ||
      !selectedOrganization ||
      !heapIdentity
    ) {
      return null;
    }

    return (
      <React.Suspense fallback={null}>
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          customerId={selectedOrganization.customerId}
          organizationId={selectedOrganization.id}
          permissions={permissions}
          heapIdentity={heapIdentity}
          getToken={getToken}
          onAlertSelect={onAlertSelect}
          onStateChange={onStateChange}
        >
          {({ alerts, onAlertSelect: handleSelect }) => (
            <>
              {alerts
                .filter(alert => alert.count > 0)
                .map(alert => (
                  <AlertTile
                    key={alert.type}
                    count={alert.count}
                    label={ALERT_LABEL_MAP[alert.type]}
                    variant={ALERT_VARIANT_MAP[alert.type]}
                    onClick={() => handleSelect(alert.type)}
                  />
                ))}
            </>
          )}
        </RemoteComponent>
      </React.Suspense>
    );
  }
);

ReservationsAlertsTiles.displayName = 'ReservationsAlertsTiles';

export { ReservationsAlertsTiles };
